import type { LineSeriesOption } from 'echarts'
import type { OnPeriod } from '@/installation/onPeriods'
import type { YAxisHighlightRange } from '@/components/charts/chartTypes'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface OnPeriodAsset {
  label: string
  onPeriods: OnPeriod[]
}

// ── Builders ──────────────────────────────────────────────────────────────────

/**
 * Mark-area series that shades every on-period for each asset.
 * Pass an empty array to produce a series with no highlights.
 */
export function buildOnPeriodHighlightSeries(
  periods: OnPeriodAsset[],
  color: string,
): LineSeriesOption {
  return {
    type: 'line' as const,
    markArea: {
      itemStyle: { color },
      label: { show: true, position: 'insideBottom' },
      data: periods.flatMap(asset =>
        asset.onPeriods.map(period => [
          { name: asset.label, coord: [period.on] },
          { coord: [period.off] },
        ])
      ),
    },
  } as LineSeriesOption
}

/**
 * Mark-area series that shades every weekend in the chart's date range.
 * Always carries a stable id so ECharts can update it in-place when the toggle
 * switches — pass an empty array to clear the highlights without removing the
 * series from the options (required with notMerge=false).
 */
export function buildWeekendHighlightSeries(
  weekends: [Date, Date][],
  color: string,
): LineSeriesOption {
  const data = weekends.map(([start, end]) => [
    { name: 'Weekend', coord: [start.toISOString()] },
    { coord: [end.toISOString()] },
  ])

  return {
    type: 'line' as const,
    id: '__weekend_highlight',
    markArea: {
      itemStyle: { color },
      label: { show: data.length > 0, position: 'insideBottom' },
      data,
    },
  } as LineSeriesOption
}

/**
 * Mark-area series that shades a horizontal band between two y-axis values,
 * useful for indicating an expected or acceptable operating range.
 * Carries a stable id so it can be updated without duplicate series accumulating.
 */
export function buildYAxisRangeHighlightSeries(
  range: YAxisHighlightRange,
  fontSize: number,
): LineSeriesOption {
  return {
    type: 'line' as const,
    id: '__yaxis_range_highlight',
    markArea: {
      silent: true,
      itemStyle: {
        color: range.color ?? 'rgba(64, 158, 255, 0.2)',
        borderColor: 'rgba(64, 158, 255, 0.8)',
        borderWidth: 1,
      },
      label: {
        show: !!range.label,
        position: 'insideRight',
        formatter: range.label ?? '',
        fontSize,
        color: '#3b82f6',
      },
      data: [[
        { name: range.label ?? 'Expected Range', yAxis: range.min, x: '0%' },
        { yAxis: range.max, x: '100%' },
      ]],
    },
  } as LineSeriesOption
}
